/**
 * Persistance de l'état de réception, pour la reprise après coupure.
 *
 * ## Le problème
 *
 * Une coupure réseau au milieu d'un fichier de 500 Mo ne doit pas coûter les 250 Mo déjà
 * reçus. Pour reprendre, il faut savoir **précisément** quels morceaux sont déjà sur le
 * disque — pas une approximation, pas un pourcentage.
 *
 * ## Le bitmap, et pas une liste de morceaux
 *
 * Répertorier les morceaux reçus coûterait des mégaoctets d'index pour un gros fichier.
 * Un bit par morceau suffit : pour des morceaux de 63 KiB, **1 Go tient en ~17 Ko de
 * bitmap**. C'est ce qui rend la persistance tenable, et c'est la conception retenue par
 * le `PLAN.md` §6.6.
 *
 * ## Ce qui est persisté, et ce qui ne l'est pas
 *
 * - Le bitmap et les métadonnées : oui, dans IndexedDB.
 * - Les octets reçus : **jamais ici**. Ils sont déjà écrits par le `FileSink` (sur disque
 *   via la File System Access API, ou en mémoire). Le dupliquer doublerait l'occupation
 *   sans rien apporter.
 * - Le `FileSystemFileHandle` : oui, quand la stratégie `fs-access` est disponible. Il est
 *   *structured-cloneable*, donc persistable, et c'est lui qui permet de **rouvrir le même
 *   fichier** plutôt que d'en créer un second à chaque tentative. En stratégie `memory`,
 *   il n'y a rien à reprendre : le tampon vit dans l'onglet et disparaît avec lui.
 *
 * ## Ce que ce module ne fait pas
 *
 * Il ne décide pas *si* une reprise est possible — c'est le rôle de `session.ts`, qui
 * connaît l'identité du fichier source. Il se contente de conserver et de rendre l'état.
 */

import { CHUNK_SIZE } from '@attrape/shared';

/** Nom de la base et du magasin. Un seul magasin : les sessions sont peu nombreuses. */
const DB_NAME = 'attrape-resume';
const STORE = 'sessions';
const DB_VERSION = 1;

/**
 * Durée de conservation d'une session non reprise.
 *
 * Le `PLAN.md` §6.6 retient 7 jours. Au-delà, un état oublié occupe de l'espace pour rien
 * et le fichier source a toutes les chances d'avoir changé.
 */
export const SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** État persisté d'une réception en cours, pour un fichier. */
export interface ResumeSession {
  /** Identifiant du fichier, tel que fourni par l'émetteur (`FileMeta.id`). */
  fileId: string;
  /** Identifiant de la salle, pour ne jamais mélanger deux partages. */
  roomId: string;
  /** Nom du fichier source — sert à vérifier qu'on reprend bien le même. */
  name: string;
  /** Taille du fichier source. */
  size: number;
  /** Date de dernière modification du fichier source. */
  lastModified: number;
  /** Bitmap des morceaux reçus : bit `n` à 1 si le morceau `n` est présent. */
  received: Uint8Array;
  /** Offset du premier morceau non reçu, pour éviter un balayage à chaque morceau. */
  firstMissing: number;
  /** Poignée de fichier persistable (stratégie `fs-access` uniquement). */
  handle?: FileSystemFileHandle | null;
  /** Horodatage de la dernière mise à jour, pour la purge. */
  updatedAt: number;
}

/** Nombre de morceaux d'un fichier de `sizeBytes` octets. */
export function chunkCountFor(sizeBytes: number): number {
  return Math.ceil(sizeBytes / CHUNK_SIZE);
}

/** Nombre d'octets nécessaires au bitmap d'un fichier de `sizeBytes` octets. */
export function bitmapBytesFor(sizeBytes: number): number {
  return Math.ceil(chunkCountFor(sizeBytes) / 8);
}

/** Crée un bitmap vide pour un fichier d'une taille donnée. */
export function createBitmap(sizeBytes: number): Uint8Array {
  return new Uint8Array(bitmapBytesFor(sizeBytes));
}

/** Le morceau `index` est-il marqué comme reçu ? */
export function isChunkReceived(bitmap: Uint8Array, index: number): boolean {
  const byte = bitmap[index >> 3];
  if (byte === undefined) return false;
  return (byte & (1 << (index & 7))) !== 0;
}

/** Marque le morceau `index` comme reçu. */
export function markChunkReceived(bitmap: Uint8Array, index: number): void {
  const byteIndex = index >> 3;
  if (byteIndex >= bitmap.length) return;
  bitmap[byteIndex] = (bitmap[byteIndex] ?? 0) | (1 << (index & 7));
}

/**
 * Convertit un bitmap en plages `[début, fin]` de morceaux manquants.
 *
 * Des plages plutôt qu'une liste d'indices : sur un fichier interrompu tôt, la liste des
 * manquants est presque aussi longue que le fichier, alors que les plages se comptent en
 * quelques dizaines. C'est ce qui garde le message de reprise petit.
 *
 * `total` est explicite et non déduit du bitmap : le dernier octet peut contenir des bits
 * de remplissage qui ne correspondent à aucun morceau réel.
 */
export function missingRanges(bitmap: Uint8Array, total: number): [number, number][] {
  const ranges: [number, number][] = [];
  let start = -1;

  for (let index = 0; index < total; index += 1) {
    const received = isChunkReceived(bitmap, index);
    if (!received && start === -1) start = index;
    if (received && start !== -1) {
      ranges.push([start, index - 1]);
      start = -1;
    }
  }
  if (start !== -1) ranges.push([start, total - 1]);

  return ranges;
}

/** Nombre de morceaux manquants, à partir des plages. */
export function missingCount(ranges: [number, number][]): number {
  let total = 0;
  for (const [start, end] of ranges) total += end - start + 1;
  return total;
}

// ─────────────────────────────────────────────────────────────────────────────
// Accès IndexedDB
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Ouvre la base. Renvoie `null` si IndexedDB est indisponible.
 *
 * Navigation privée, politique d'entreprise, quota refusé : l'indisponibilité du stockage
 * ne doit **jamais** empêcher un transfert. Elle prive seulement de la reprise — ce qui
 * est un confort, pas une fonction. Un échec ici est donc silencieux par conception, et
 * c'est délibéré plutôt que négligent.
 */
function openDatabase(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }

    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'fileId' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
}

/**
 * Enregistre l'état d'une session.
 *
 * Appelé à chaque morceau : IndexedDB écrit de façon asynchrone et groupée, donc le coût
 * par morceau reste faible. Le `PLAN.md` §6.6 borne néanmoins le besoin — on ne marque
 * qu'un bit, pas un fichier.
 */
export async function saveSession(session: ResumeSession): Promise<void> {
  const db = await openDatabase();
  if (!db) return;

  try {
    await new Promise<void>((resolve) => {
      const transaction = db.transaction(STORE, 'readwrite');
      transaction.objectStore(STORE).put({ ...session, updatedAt: Date.now() });
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => resolve();
      transaction.onabort = () => resolve();
    });
  } finally {
    db.close();
  }
}

/** Récupère l'état d'une session, ou `null` s'il n'existe pas ou n'est plus valable. */
export async function loadSession(fileId: string): Promise<ResumeSession | null> {
  const db = await openDatabase();
  if (!db) return null;

  try {
    const stored = await new Promise<ResumeSession | undefined>((resolve) => {
      const transaction = db.transaction(STORE, 'readonly');
      const request = transaction.objectStore(STORE).get(fileId);
      request.onsuccess = () => resolve(request.result as ResumeSession | undefined);
      request.onerror = () => resolve(undefined);
    });

    if (!stored) return null;

    // Un état périmé est ignoré plutôt que repris : le fichier source a pu changer, et
    // reprendre sur une base fausse produirait un fichier silencieusement corrompu.
    if (Date.now() - stored.updatedAt > SESSION_TTL_MS) {
      await deleteSession(fileId);
      return null;
    }

    // `structuredClone` d'IndexedDB peut rendre un `Uint8Array` : on garantit le type,
    // car `isChunkReceived` indexe le tableau et échouerait sur un `ArrayBuffer` brut.
    return { ...stored, received: new Uint8Array(stored.received) };
  } finally {
    db.close();
  }
}

/** Supprime l'état d'une session (transfert terminé, ou état invalide). */
export async function deleteSession(fileId: string): Promise<void> {
  const db = await openDatabase();
  if (!db) return;

  try {
    await new Promise<void>((resolve) => {
      const transaction = db.transaction(STORE, 'readwrite');
      transaction.objectStore(STORE).delete(fileId);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => resolve();
      transaction.onabort = () => resolve();
    });
  } finally {
    db.close();
  }
}

/**
 * Purge les sessions expirées.
 *
 * Appelé au démarrage plutôt qu'à intervalle régulier : la seule chose qui compte est
 * qu'un état oublié ne survive pas indéfiniment. Un balayage périodique consommerait un
 * minuteur pour aucun bénéfice.
 */
export async function purgeExpiredSessions(): Promise<number> {
  const db = await openDatabase();
  if (!db) return 0;

  try {
    const all = await new Promise<ResumeSession[]>((resolve) => {
      const transaction = db.transaction(STORE, 'readonly');
      const request = transaction.objectStore(STORE).getAll();
      request.onsuccess = () => resolve(request.result as ResumeSession[]);
      request.onerror = () => resolve([]);
    });

    const now = Date.now();
    const expired = all.filter((session) => now - session.updatedAt > SESSION_TTL_MS);
    for (const session of expired) await deleteSession(session.fileId);
    return expired.length;
  } finally {
    db.close();
  }
}
